import {
  DownloadOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePptOutlined,
  FileWordOutlined
} from "@ant-design/icons"
import { Alert, Button, Modal, Space, Spin } from "antd"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { downloadFile } from "../../utils/download-file.ts"
import { PdfViewer } from "./PdfViewer.tsx"

type FileType =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "office"
  | "text"
  | "unknown"

interface FileDoc {
  file: string
  file_name?: string
  name?: string
  original_filename?: string
  response?: { original_filename?: string }
  version?: number
}

const pickNonEmpty = (v: string | undefined | null): string | undefined => {
  const s = v?.trim()
  return s ? s : undefined
}

const filenameFromUrl = (url: string): string | undefined => {
  if (!url) return undefined
  try {
    const path = new URL(url).pathname
    const segment = path.split("/").filter(Boolean).pop()
    if (!segment) return undefined
    return decodeURIComponent(segment)
  } catch {
    return undefined
  }
}

const resolveDisplayFileName = (doc: FileDoc | null): string => {
  if (!doc) return "File"
  const candidates = [
    pickNonEmpty(doc.file_name),
    pickNonEmpty(doc.name),
    pickNonEmpty(doc.original_filename),
    pickNonEmpty(doc.response?.original_filename)
  ]
  for (const c of candidates) {
    if (c) return c
  }
  const fromUrl = filenameFromUrl(doc.file)
  if (fromUrl) return fromUrl
  return "File"
}

interface FilePreviewModalProps {
  visible: boolean
  onClose: () => void
  doc: FileDoc | null
}

const FILE_EXTENSIONS: Record<FileType, string[]> = {
  image: [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".bmp", ".ico"],
  video: [".mp4", ".mov", ".webm", ".avi", ".mkv", ".m4v"],
  audio: [".mp3", ".wav", ".ogg", ".m4a", ".flac", ".aac"],
  pdf: [".pdf"],
  office: [".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".csv"],
  text: [".txt", ".json", ".xml", ".md", ".html", ".css", ".js", ".ts"],
  unknown: []
}

export const getFileType = (url: string, fileName?: string): FileType => {
  for (const candidate of [url, fileName]) {
    if (!candidate) continue
    const lower = candidate.toLowerCase()
    for (const [type, extensions] of Object.entries(FILE_EXTENSIONS)) {
      if (extensions.some((ext) => lower.includes(ext))) {
        return type as FileType
      }
    }
  }

  return "unknown"
}

// officeapps.live.com fetches the src server-side, so a blob: URL is unreachable to it
export const resolvePreviewType = (
  url: string,
  fileName?: string
): FileType => {
  const type = getFileType(url, fileName)
  if (type === "office" && !/^https?:/i.test(url)) return "unknown"
  return type
}

const getOfficeIcon = (url: string) => {
  const lowerUrl = url.toLowerCase()
  if (lowerUrl.includes(".doc"))
    return <FileWordOutlined style={{ fontSize: 64, color: "#2b579a" }} />
  if (lowerUrl.includes(".xls") || lowerUrl.includes(".csv"))
    return <FileExcelOutlined style={{ fontSize: 64, color: "#217346" }} />
  if (lowerUrl.includes(".ppt"))
    return <FilePptOutlined style={{ fontSize: 64, color: "#d24726" }} />
  return <FileOutlined style={{ fontSize: 64 }} />
}

const Loader = () => (
  <div
    style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1
    }}
  >
    <Spin size="large" />
  </div>
)

export const FilePreviewModal = ({
  visible,
  onClose,
  doc
}: FilePreviewModalProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [textContent, setTextContent] = useState<string | null>(null)

  const name = resolveDisplayFileName(doc)
  const fileUrl = doc?.file || ""

  const fileType = useMemo(() => {
    if (!fileUrl) return "unknown"
    return resolvePreviewType(fileUrl, name)
  }, [fileUrl, name])

  useEffect(() => {
    if (!visible || !fileUrl) return

    setLoading(true)
    setError(null)
    setTextContent(null)

    if (fileType === "text") {
      fetch(fileUrl)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch")
          return res.text()
        })
        .then((text) => {
          setTextContent(text)
          setLoading(false)
        })
        .catch(() => {
          setError("Failed to load text file")
          setLoading(false)
        })
    }
  }, [visible, fileUrl, fileType])

  const handleLoad = () => setLoading(false)
  const handleError = (msg: string) => {
    setLoading(false)
    setError(msg)
  }

  const handleDownload = async () => {
    if (!fileUrl) return
    await downloadFile(fileUrl, name)
  }

  const renderContent = () => {
    if (error) {
      return (
        <Alert
          message="Error loading file"
          description={error}
          type="error"
          showIcon
        />
      )
    }

    switch (fileType) {
      case "image":
        return (
          <div style={{ textAlign: "center", position: "relative" }}>
            {loading && <Loader />}
            <img
              src={fileUrl}
              alt={name}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                display: "block",
                margin: "0 auto"
              }}
              onLoad={handleLoad}
              onError={() => handleError("Failed to load image")}
            />
          </div>
        )

      case "video":
        return (
          <div style={{ textAlign: "center", position: "relative" }}>
            {loading && <Loader />}
            <video
              src={fileUrl}
              controls
              preload="metadata"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                borderRadius: 6,
                display: loading ? "none" : "block",
                margin: "0 auto"
              }}
              onLoadedData={handleLoad}
              onError={() => handleError("Failed to load video")}
            />
          </div>
        )

      case "audio":
        return (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <audio
              src={fileUrl}
              controls
              style={{ width: "100%", maxWidth: 500 }}
              onLoadedData={handleLoad}
              onError={() => handleError("Failed to load audio")}
            />
          </div>
        )

      case "pdf": {
        return <PdfViewer fileUrl={fileUrl} name={name} onLoad={handleLoad} />
      }

      case "office":
        // The embed is a Microsoft-hosted page that fetches `fileUrl` on its
        // own server, outside this browser — its `onLoad` only confirms that
        // page itself loaded, not that it could reach and render our file
        // (it cannot reach a local dev URL at all, for instance). There is no
        // cross-origin way to tell success from a silent failure inside it,
        // so the fallback stays visible rather than only appearing on a
        // failure this code can't actually detect.
        return (
          <div>
            <div style={{ height: "60vh", position: "relative" }}>
              {loading && <Loader />}
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: 6
                }}
                title={name}
                onLoad={handleLoad}
                onError={() => handleError("Failed to load document")}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 0 0",
                color: "#666"
              }}
            >
              <span>{t("filePreview.officeFallbackPrompt")}</span>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
              >
                {t("filePreview.downloadInstead")}
              </Button>
            </div>
          </div>
        )

      case "text":
        return (
          <div style={{ position: "relative" }}>
            {loading && <Loader />}
            {textContent && (
              <pre
                style={{
                  background: "#f5f5f5",
                  padding: 16,
                  borderRadius: 6,
                  maxHeight: "70vh",
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                  fontSize: 13
                }}
              >
                {textContent}
              </pre>
            )}
          </div>
        )

      default:
        return (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {getOfficeIcon(fileUrl)}
            <p style={{ marginTop: 16, color: "#666" }}>
              Preview not available for this file type
            </p>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Download File
            </Button>
          </div>
        )
    }
  }

  const title = `Preview: ${name}${doc?.version && doc.version > 1 ? ` (v${doc.version})` : ""}`

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      width="80%"
      style={{ top: 20, maxWidth: 1000 }}
      destroyOnHidden
      footer={
        <Actions onDownloadClick={handleDownload} onCloseClick={onClose} />
      }
      styles={{
        body: {
          padding: "16px 0 0",
          maxHeight: "80vh",
          overflow: "auto"
        }
      }}
    >
      {renderContent()}
    </Modal>
  )
}

interface ActionsProps {
  onDownloadClick: () => Promise<void>
  onCloseClick: () => void
}

const Actions = ({ onDownloadClick, onCloseClick }: ActionsProps) => {
  const [downloading, setDownloading] = useState(false)

  const onDownloadClickHandler = async () => {
    try {
      setDownloading(true)
      await onDownloadClick()
    } catch (error) {
      console.error("Failed to download file:", error)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Space>
      <Button
        loading={downloading}
        icon={<DownloadOutlined />}
        onClick={onDownloadClickHandler}
      >
        Download
      </Button>
      <Button disabled={downloading} onClick={onCloseClick}>
        Close
      </Button>
    </Space>
  )
}

export default FilePreviewModal
